import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, RotateCcw, Users, CheckCircle, XCircle, Crown, Clock, Shield, ChevronDown, Tag, X, ChevronUp, StickyNote, Edit3, Lock, Unlock, Lightbulb, LayoutGrid, Table2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { GAME_CONFIGS, ROLES, RoleType } from '../types';
import FloatingTimer from '../components/FloatingTimer';
import { 
  saveGameState, 
  loadGameState, 
  clearGameState,
  savePlayerNotes,
  loadPlayerNotes,
  saveGameLockState,
  loadGameLockState,
  saveMissionResults,
  loadMissionResults,
  savePlayerRoleMarks,
  loadPlayerRoleMarks,
  PlayerNotes
} from '../utils/gameStorage';

interface VoteRecord {
  round: number;
  teamSize: number;
  leader: string;
  team: string[];
  votes: Record<string, 'approve' | 'reject'>;
  result: 'approved' | 'rejected' | null;
  isForceRound?: boolean;
  missionResult?: MissionResult;
  missionFailVotes?: number;
}

type MissionResult = 'pending' | 'success' | 'fail';

interface MissionRecord {
  result: MissionResult;
  failVotes?: number;
}

interface EditingVote {
  index: number;
  vote: VoteRecord;
}

export default function MissionVote() {
  const navigate = useNavigate();
  
  const [isGameLocked, setIsGameLocked] = useState(false);
  
  const [playerCount, setPlayerCount] = useState<number>(5);
  const [players, setPlayers] = useState<string[]>(['1号', '2号', '3号', '4号', '5号']);
  const [currentRound, setCurrentRound] = useState(1);
  const [leaderIndex, setLeaderIndex] = useState(0);
  const [votes, setVotes] = useState<VoteRecord[]>([]);
  const [currentVote, setCurrentVote] = useState<VoteRecord | null>(null);
  const [showFloatingTimer, setShowFloatingTimer] = useState(false);
  const [missionResults, setMissionResults] = useState<MissionRecord[]>([
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
    { result: 'pending' },
  ]);
  const [showLeaderSelector, setShowLeaderSelector] = useState(true);
  const [playerRoleMarks, setPlayerRoleMarks] = useState<Record<number, RoleType[]>>({});
  const [showRoleSelector, setShowRoleSelector] = useState<number | null>(null);
  const [isVotePanelOpen, setIsVotePanelOpen] = useState(false);
  const votePanelRef = useRef<HTMLDivElement>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isPlayerListCollapsed, setIsPlayerListCollapsed] = useState(false);
  
  const [isGuideCollapsed, setIsGuideCollapsed] = useState(true);
  
  // V138: 投票记录视图模式（卡片视图/纵向对比视图）
  const [voteViewMode, setVoteViewMode] = useState<'card' | 'vertical'>('card');
  
  const [isNotePanelOpen, setIsNotePanelOpen] = useState(false);
  const [playerNotes, setPlayerNotes] = useState<PlayerNotes>({ public: '' });
  const [activeNoteTab, setActiveNoteTab] = useState<string>('public');
  const [notePosition, setNotePosition] = useState({ x: 20, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const notePanelRef = useRef<HTMLDivElement>(null);

  const [editingVote, setEditingVote] = useState<EditingVote | null>(null);
  const [showEditVoteModal, setShowEditVoteModal] = useState(false);
  
  const [showMissionResultModal, setShowMissionResultModal] = useState(false);
  const [selectedVoteIndex, setSelectedVoteIndex] = useState<number | null>(null);
  const [tempMissionResult, setTempMissionResult] = useState<MissionResult>('pending');
  const [tempMissionFailVotes, setTempMissionFailVotes] = useState<number>(1);

  const config = GAME_CONFIGS[playerCount];
  const requiredTeamSize = config.missions[currentRound - 1] || config.missions[0];

  useEffect(() => {
    const savedState = loadGameState();
    if (savedState) {
      setPlayerCount(savedState.playerCount);
      setPlayers(savedState.players);
      setCurrentRound(savedState.currentRound);
      setLeaderIndex(savedState.leaderIndex);
      setVotes(savedState.votes || []);
      setPlayerRoleMarks(savedState.playerRoleMarks || {});
    }
    const savedNotes = loadPlayerNotes();
    setPlayerNotes(savedNotes);
    const savedLockState = loadGameLockState();
    setIsGameLocked(savedLockState);
    const savedMissionResults = loadMissionResults();
    if (savedMissionResults) {
      setMissionResults(savedMissionResults);
    }
    const savedRoleMarks = loadPlayerRoleMarks();
    if (savedRoleMarks && Object.keys(savedRoleMarks).length > 0) {
      setPlayerRoleMarks(savedRoleMarks);
    }
  }, []);

  useEffect(() => {
    if (players.length > 0) {
      saveGameState({
        playerCount,
        players,
        currentRound,
        leaderIndex,
        votes,
        missionResults,
        playerRoleMarks,
        lastUpdated: Date.now(),
      });
    }
  }, [playerCount, players, currentRound, leaderIndex, votes, missionResults, playerRoleMarks]);

  useEffect(() => {
    savePlayerNotes(playerNotes);
  }, [playerNotes]);

  useEffect(() => {
    saveGameLockState(isGameLocked);
  }, [isGameLocked]);

  const updatePlayerCount = (count: number) => {
    if (isGameLocked) return;
    setPlayerCount(count);
    const newPlayers: string[] = [];
    for (let i = 0; i < count; i++) {
      newPlayers.push(`${i + 1}号`);
    }
    setPlayers(newPlayers);
    const newNotes: PlayerNotes = { public: playerNotes.public };
    setPlayerNotes(newNotes);
    resetGame();
  };

  const toggleGameLock = () => {
    if (!isGameLocked) {
      setIsGameLocked(true);
    } else {
      setIsGameLocked(false);
    }
  };

  const startVoting = () => {
    setCurrentVote({
      round: currentRound,
      teamSize: requiredTeamSize,
      leader: players[leaderIndex],
      team: [],
      votes: {},
      result: null,
    });
    setIsVotePanelOpen(true);
    setTimeout(() => {
      votePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const toggleVotePanel = () => {
    if (isVotePanelOpen) {
      setIsVotePanelOpen(false);
      setCurrentVote(null);
    } else {
      startVoting();
    }
  };

  const toggleTeamMember = (player: string) => {
    if (!currentVote) return;

    const isInTeam = currentVote.team.includes(player);
    if (isInTeam) {
      setCurrentVote({
        ...currentVote,
        team: currentVote.team.filter((p) => p !== player),
      });
    } else if (currentVote.team.length < requiredTeamSize) {
      setCurrentVote({
        ...currentVote,
        team: [...currentVote.team, player],
      });
    }
  };

  const castVote = (player: string, vote: 'approve' | 'reject') => {
    if (!currentVote) return;
    setCurrentVote({
      ...currentVote,
      votes: { ...currentVote.votes, [player]: vote },
    });
  };

  const finishVoting = () => {
    if (!currentVote) return;

    const approveCount = Object.values(currentVote.votes).filter((v) => v === 'approve').length;
    const rejectCount = Object.values(currentVote.votes).filter((v) => v === 'reject').length;
    const result: 'approved' | 'rejected' = approveCount > rejectCount ? 'approved' : 'rejected';

    const finalVote: VoteRecord = { ...currentVote, result };
    setVotes([...votes, finalVote]);

    if (result === 'approved') {
      if (currentRound < 5) {
        setCurrentRound(currentRound + 1);
      }
    }

    setLeaderIndex((leaderIndex + 1) % playerCount);
    setIsVotePanelOpen(false);
    setCurrentVote(null);
  };

  const finishForceRound = () => {
    if (!currentVote) return;

    const finalVote: VoteRecord = { 
      ...currentVote, 
      result: 'approved',
      isForceRound: true 
    };
    setVotes([...votes, finalVote]);

    if (currentRound < 5) {
      setCurrentRound(currentRound + 1);
    }

    setLeaderIndex((leaderIndex + 1) % playerCount);
    setIsVotePanelOpen(false);
    setCurrentVote(null);
  };

  const startEditVote = (index: number) => {
    setEditingVote({ index, vote: { ...votes[index] } });
    setShowEditVoteModal(true);
  };

  const saveEditVote = () => {
    if (!editingVote) return;
    const newVotes = [...votes];
    newVotes[editingVote.index] = editingVote.vote;
    setVotes(newVotes);
    setShowEditVoteModal(false);
    setEditingVote(null);
  };

  const togglePlayerRole = (playerIndex: number, roleId: RoleType) => {
    const currentRoles = playerRoleMarks[playerIndex] || [];
    let newRoles: RoleType[];
    
    if (currentRoles.includes(roleId)) {
      newRoles = currentRoles.filter(r => r !== roleId);
    } else {
      if (currentRoles.length >= 3) {
        return;
      }
      newRoles = [...currentRoles, roleId];
    }
    
    const newMarks = { ...playerRoleMarks, [playerIndex]: newRoles };
    setPlayerRoleMarks(newMarks);
    savePlayerRoleMarks(newMarks);
  };

  const resetGame = () => {
    setCurrentRound(1);
    setLeaderIndex(0);
    setVotes([]);
    setCurrentVote(null);
    setMissionResults([
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
      { result: 'pending' },
    ]);
    setPlayerRoleMarks({});
    setIsVotePanelOpen(false);
    setIsPlayerListCollapsed(false);
    setIsGameLocked(false);
    setPlayerNotes({ public: '' });
    setActiveNoteTab('public');
    localStorage.removeItem('avalon_mission_results');
    localStorage.removeItem('avalon_player_role_marks');
    clearGameState();
  };

  const openMissionResultModal = (voteIndex: number) => {
    const vote = votes[voteIndex];
    if (vote.result !== 'approved') return;
    
    setSelectedVoteIndex(voteIndex);
    setTempMissionResult(vote.missionResult || 'pending');
    setTempMissionFailVotes(vote.missionFailVotes || 1);
    setShowMissionResultModal(true);
  };

  const saveMissionResult = () => {
    if (selectedVoteIndex === null) return;
    
    const newVotes = [...votes];
    newVotes[selectedVoteIndex] = {
      ...newVotes[selectedVoteIndex],
      missionResult: tempMissionResult,
      missionFailVotes: tempMissionResult === 'fail' ? tempMissionFailVotes : undefined,
    };
    setVotes(newVotes);
    
    const vote = newVotes[selectedVoteIndex];
    const round = vote.round;
    const newMissionResults = [...missionResults];
    newMissionResults[round - 1] = {
      result: tempMissionResult,
      failVotes: tempMissionResult === 'fail' ? tempMissionFailVotes : undefined,
    };
    setMissionResults(newMissionResults);
    saveMissionResults(newMissionResults);
    
    setShowMissionResultModal(false);
    setSelectedVoteIndex(null);
  };

  const clearMissionResult = () => {
    if (selectedVoteIndex === null) return;
    
    const newVotes = [...votes];
    newVotes[selectedVoteIndex] = {
      ...newVotes[selectedVoteIndex],
      missionResult: undefined,
      missionFailVotes: undefined,
    };
    setVotes(newVotes);
    
    const vote = newVotes[selectedVoteIndex];
    const round = vote.round;
    const newMissionResults = [...missionResults];
    newMissionResults[round - 1] = { result: 'pending' };
    setMissionResults(newMissionResults);
    saveMissionResults(newMissionResults);
    
    setShowMissionResultModal(false);
    setSelectedVoteIndex(null);
  };

  const getVoteStats = (vote: VoteRecord) => {
    const approve = Object.values(vote.votes).filter((v) => v === 'approve').length;
    const reject = Object.values(vote.votes).filter((v) => v === 'reject').length;
    return { approve, reject };
  };

  // V115: 获取投票详情 - 按玩家号码数字排序
  const getVoteDetails = (vote: VoteRecord) => {
    const approvePlayers: string[] = [];
    const rejectPlayers: string[] = [];
    Object.entries(vote.votes).forEach(([player, v]) => {
      if (v === 'approve') approvePlayers.push(player);
      else rejectPlayers.push(player);
    });
    // V115: 按数字排序（提取号码后按数字比较）
    const sortByNumber = (a: string, b: string) => {
      const numA = parseInt(a.replace(/[^0-9]/g, ''));
      const numB = parseInt(b.replace(/[^0-9]/g, ''));
      return numA - numB;
    };
    approvePlayers.sort(sortByNumber);
    rejectPlayers.sort(sortByNumber);
    return { approvePlayers, rejectPlayers };
  };

  const getAvailableRoles = (): RoleType[] => {
    if (playerCount === 5) {
      return ['merlin', 'percival', 'loyal', 'morgana', 'assassin'];
    } else if (playerCount <= 10) {
      return ['merlin', 'percival', 'loyal', 'mordred', 'morgana', 'assassin', 'minion', 'oberon'];
    } else {
      return ['merlin', 'percival', 'loyal', 'mordred', 'morgana', 'assassin', 'minion', 'oberon', 'goodLancelot', 'evilLancelot'];
    }
  };

  const updateNoteContent = (content: string) => {
    setPlayerNotes({
      ...playerNotes,
      [activeNoteTab]: content
    });
  };

  const getCurrentNoteContent = () => {
    return playerNotes[activeNoteTab] || '';
  };

  const getPlayerTabName = (index: number) => {
    const playerName = players[index] || `${index + 1}号`;
    return `${index + 1}号${playerName !== `${index + 1}号` ? playerName : '玩家'}`;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (notePanelRef.current && (e.target as HTMLElement).closest('.note-drag-handle')) {
      setIsDragging(true);
      const rect = notePanelRef.current.getBoundingClientRect();
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && notePanelRef.current) {
      const newX = window.innerWidth - (e.clientX - dragOffset.current.x) - 320;
      const newY = e.clientY - dragOffset.current.y;
      setNotePosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - 320)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 400)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <ThemedBackground pageName="mission-vote">
    <div className="min-h-screen p-5 pb-8">
      {/* V189: 固定头部 */}
      <div className="sticky top-0 z-50 -mx-5 px-5 py-2 bg-gradient-to-b from-[#1A0F2A] via-[#1A0F2A]/95 to-transparent flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">游戏记录</h1>
          <p className="text-avalon-text text-xs">第 {currentRound} 轮 | 队长: {players[leaderIndex]}</p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={() => setShowFloatingTimer(true)}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
            title="发言计时"
          >
            <Clock className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsNotePanelOpen(true)}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
            title="游戏笔记"
          >
            <StickyNote className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowResetConfirm(true)}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white transition-colors"
            title="重置游戏"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mb-4 bg-gradient-to-r from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-3">
        <button
          onClick={() => setIsGuideCollapsed(!isGuideCollapsed)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-blue-500/30 flex items-center justify-center">
              <Lightbulb className="w-3.5 h-3.5 text-blue-400" />
            </div>
            <span className="text-white font-medium text-sm">使用指南</span>
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-avalon-text transition-transform duration-200 ${isGuideCollapsed ? '' : 'rotate-180'}`}
          />
        </button>
        
        {!isGuideCollapsed && (
          <div className="mt-3 pt-3 border-t border-blue-500/20 space-y-3 text-avalon-text text-xs leading-relaxed">
            <div>
              <span className="text-blue-400 font-medium">【操作流程】</span>
              <div className="mt-1 space-y-1">
                <div>①锁定人数 → ②选择初始队长 → ③开始投票→④勾选组队人选→⑤记录赞同反对票→⑥填写任务结果</div>
              </div>
            </div>
            <div>
              <span className="text-amber-400 font-medium">【额外功能】</span>
              <div className="mt-1 space-y-1">
                <div>①发言倒计时：右上角倒计时按钮，可启动倒计时浮窗进行计时</div>
                <div>②玩家笔记：右上角笔记按钮，可打开笔记页面，可针对公共信息和玩家发言分别记录</div>
                <div>③角色身份标记：玩家列表右侧，有按钮可用于标记角色身份，最多标记3个</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-avalon-text text-sm">游戏人数</div>
          {isGameLocked && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/30 text-green-400 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              已锁定
            </span>
          )}
        </div>
        <div className="grid grid-cols-5 gap-2 mb-2">
          {[5, 6, 7, 8, 9].map((count) => (
            <button
              key={count}
              onClick={() => updatePlayerCount(count)}
              disabled={isGameLocked}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${
                playerCount === count
                  ? 'bg-avalon-gold text-avalon-bg-dark'
                  : 'bg-avalon-card text-white hover:bg-avalon-purple'
              } ${isGameLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {count}人
            </button>
          ))}
        </div>
        <div className="grid grid-cols-5 gap-2 mb-2">
          {[10, 11, 12, 13].map((count) => (
            <button
              key={count}
              onClick={() => updatePlayerCount(count)}
              disabled={isGameLocked}
              className={`py-2 rounded-lg text-sm font-medium transition-all ${
                playerCount === count
                  ? 'bg-avalon-gold text-avalon-bg-dark'
                  : 'bg-avalon-card text-white hover:bg-avalon-purple'
              } ${isGameLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {count}人
            </button>
          ))}
          <button
            onClick={toggleGameLock}
            className={`py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
              isGameLocked
                ? 'bg-amber-500/30 text-amber-400 hover:bg-amber-500/50'
                : 'bg-green-500/30 text-green-400 hover:bg-green-500/50'
            }`}
          >
            {isGameLocked ? (
              <>
                <Unlock className="w-4 h-4" />
                <span>解锁</span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                <span>锁定</span>
              </>
            )}
          </button>
        </div>
        {!isGameLocked && (
          <p className="text-avalon-text text-xs text-center mt-2">
            请先选择游戏人数，然后点击锁定按钮
          </p>
        )}
      </div>

      <div className="avalon-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">任务进程</h3>
          <span className="text-avalon-text text-xs">点击记录任务结果</span>
        </div>
        <div className="flex justify-between gap-2">
          {config.missions.map((missionSize, index) => {
            const round = index + 1;
            const record = missionResults[index];
            const isProtected = round === 4;
            return (
              <button
                key={round}
                onClick={() => {}}
                disabled={true}
                className={`flex-1 py-2 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all ${
                  record.result === 'success'
                    ? 'bg-blue-500 text-white'
                    : record.result === 'fail'
                    ? 'bg-red-500 text-white'
                    : 'bg-avalon-purple text-white'
                }`}
              >
                <span className="text-[10px] opacity-80">第{round}轮</span>
                <span className="text-sm font-medium flex items-center gap-1">
                  {missionSize}人
                  {isProtected && (
                    <Shield className="w-3.5 h-3.5 text-avalon-gold" />
                  )}
                </span>
                {record.result === 'success' && (
                  <span className="text-[10px] opacity-90">成功</span>
                )}
                {record.result === 'fail' && (
                  <span className="text-[10px] opacity-90">{record.failVotes || 1}反对</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="avalon-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-avalon-gold" />
            玩家列表
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPlayerListCollapsed(!isPlayerListCollapsed)}
              className="p-1.5 rounded-lg text-avalon-text hover:text-avalon-gold hover:bg-avalon-purple/50 transition-all duration-300"
              title={isPlayerListCollapsed ? '展开玩家列表' : '收起玩家列表'}
            >
              {isPlayerListCollapsed ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isPlayerListCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-avalon-text text-xs">设置初始队长</span>
            <button
              onClick={() => setShowLeaderSelector(!showLeaderSelector)}
              className="flex items-center gap-1 text-avalon-gold text-xs hover:text-yellow-400 transition-colors"
            >
              {players[leaderIndex]}
              <ChevronDown className={`w-3 h-3 transition-transform ${showLeaderSelector ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {showLeaderSelector && (
            <div className="grid grid-cols-5 gap-2 p-2 bg-avalon-bg-dark/50 rounded-lg mb-4">
              {players.map((player, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setLeaderIndex(index);
                    setShowLeaderSelector(false);
                  }}
                  className={`py-2 rounded text-sm font-medium transition-colors ${
                    index === leaderIndex
                      ? 'bg-avalon-gold text-avalon-bg-dark'
                      : 'bg-avalon-card text-white hover:bg-avalon-purple'
                  }`}
                >
                  {player}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
          {players.map((player, index) => {
            const markedRoles = playerRoleMarks[index] || [];
            const isLeader = index === leaderIndex;
            return (
              <div
                key={index}
                className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${
                  isLeader
                    ? 'bg-avalon-gold/20 border border-avalon-gold'
                    : 'bg-avalon-bg-dark/50'
                }`}
              >
                {isLeader && <Crown className="w-4 h-4 text-avalon-gold flex-shrink-0" />}
                
                <div className="flex-1 min-w-0">
                  <span className={`text-sm truncate ${isLeader ? 'text-avalon-gold font-medium' : 'text-white'}`}>
                    {player}
                  </span>
                </div>

                {/* V91: 优化角色标记展示 - 每个标记独立圆形底框 */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {markedRoles.length > 0 ? (
                    markedRoles.map((roleId) => (
                      <button
                        key={roleId}
                        onClick={() => setShowRoleSelector(index)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-sm transition-all ${
                          ROLES[roleId]?.faction === 'good'
                            ? 'bg-blue-500/30 border border-blue-500/50 text-blue-400 hover:bg-blue-500/50'
                            : 'bg-red-500/30 border border-red-500/50 text-red-400 hover:bg-red-500/50'
                        }`}
                        title={ROLES[roleId]?.name}
                      >
                        {ROLES[roleId]?.icon}
                      </button>
                    ))
                  ) : (
                    <button
                      onClick={() => setShowRoleSelector(index)}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all bg-avalon-purple/50 text-avalon-text hover:bg-avalon-purple hover:text-white"
                      title="标记身份"
                    >
                      <Tag className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
        {isPlayerListCollapsed && (
          <div className="text-center text-avalon-text text-sm py-2">
            玩家列表已收起（{players.length}人）
          </div>
        )}
      </div>

      <div className="mb-6">
        <button
          onClick={toggleVotePanel}
          className={`w-full py-2.5 text-lg font-medium rounded-xl flex items-center justify-center gap-2 transition-all ${
            isVotePanelOpen
              ? 'bg-avalon-purple text-white'
              : 'avalon-btn'
          }`}
        >
          <span>{isVotePanelOpen ? `收起第 ${currentRound} 轮投票` : `开始第 ${currentRound} 轮投票`}</span>
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isVotePanelOpen ? 'rotate-180' : ''}`} />
        </button>

        <div
          ref={votePanelRef}
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isVotePanelOpen ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          {currentVote && (
            <div className="avalon-card-lg">
              <h3 className="text-white font-semibold mb-4">
                选择任务队员 ({currentVote.team.length}/{requiredTeamSize})
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {players.map((player) => {
                  const isSelected = currentVote.team.includes(player);
                  const isLeader = player === currentVote.leader;
                  return (
                    <button
                      key={player}
                      onClick={() => toggleTeamMember(player)}
                      disabled={!isSelected && currentVote.team.length >= requiredTeamSize}
                      className={`p-3 rounded-lg flex items-center gap-2 transition-all ${
                        isSelected
                          ? 'bg-blue-500/30 border border-blue-500 text-white'
                          : isLeader
                          ? 'bg-avalon-gold/20 border border-avalon-gold text-avalon-gold'
                          : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-avalon-purple/30'
                      } disabled:opacity-50`}
                    >
                      {isLeader && <Crown className="w-4 h-4" />}
                      <span className="text-sm">{player}</span>
                      {isSelected && <CheckCircle className="w-4 h-4 ml-auto" />}
                    </button>
                  );
                })}
              </div>

              {currentVote.team.length === requiredTeamSize && (
                <>
                  <div className="border-t border-avalon-purple/30 pt-4 mb-4">
                    <h4 className="text-white font-medium mb-2">投票表决</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {players.map((player) => {
                        const vote = currentVote.votes[player];
                        return (
                          <div
                            key={player}
                            className={`p-3 rounded-lg flex items-center justify-between ${
                              vote === 'approve'
                                ? 'bg-blue-500/20 border border-blue-500'
                                : vote === 'reject'
                                ? 'bg-red-500/20 border border-red-500'
                                : 'bg-avalon-bg-dark/50'
                            }`}
                          >
                            <span className="text-sm text-white">{player}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => castVote(player, 'approve')}
                                className={`p-1.5 rounded ${
                                  vote === 'approve' ? 'bg-blue-500 text-white' : 'bg-avalon-purple text-avalon-text'
                                }`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => castVote(player, 'reject')}
                                className={`p-1.5 rounded ${
                                  vote === 'reject' ? 'bg-red-500 text-white' : 'bg-avalon-purple text-avalon-text'
                                }`}
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* V91: 投票表决区域按钮始终可见 */}
                  <div className="flex gap-3">
                    <button 
                      onClick={finishForceRound} 
                      className="flex-1 py-2 rounded-lg bg-amber-500/30 border border-amber-500 text-amber-400 font-medium hover:bg-amber-500/50 transition-colors"
                    >
                      强制轮
                    </button>
                    <button 
                      onClick={finishVoting} 
                      disabled={Object.keys(currentVote.votes).length !== playerCount}
                      className="flex-1 avalon-btn py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      确认投票结果
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* V138: 投票记录 - 支持卡片视图和纵向对比视图切换 */}
      {votes.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">投票记录</h3>
            {/* 视图切换按钮 */}
            <div className="flex items-center gap-1 bg-avalon-card rounded-lg p-1">
              <button
                onClick={() => setVoteViewMode('card')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  voteViewMode === 'card'
                    ? 'bg-avalon-purple text-white'
                    : 'text-avalon-text hover:text-white'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                卡片视图
              </button>
              <button
                onClick={() => setVoteViewMode('vertical')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  voteViewMode === 'vertical'
                    ? 'bg-avalon-purple text-white'
                    : 'text-avalon-text hover:text-white'
                }`}
              >
                <Table2 className="w-4 h-4" />
                纵向对比
              </button>
            </div>
          </div>
          
          {/* V190: 纵向对比视图 - 显示每次投票尝试，组队只显示队员 */}
          {voteViewMode === 'vertical' && (
            <div className="avalon-card overflow-hidden p-3">
              <div className="text-white font-medium mb-2 pb-1.5 border-b border-avalon-purple/20 text-sm flex items-center justify-between">
                <span>投票记录 - 纵向对比视图</span>
                <span className="text-avalon-text text-xs">显示每次投票尝试</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[280px]">
                  <thead>
                    <tr className="border-b border-avalon-purple/20">
                      <th className="text-left py-1 px-1.5 text-avalon-text text-xs font-medium sticky left-0 bg-avalon-card z-10 min-w-[50px]">
                        玩家
                      </th>
                      {/* V190: 显示所有投票记录，包括每次尝试 */}
                      {votes.map((vote, index) => {
                        // V128: 根据任务结果添加背景色
                        const hasMissionResult = vote.missionResult && vote.missionResult !== 'pending';
                        const headerClass = hasMissionResult
                          ? vote.missionResult === 'success'
                            ? 'bg-blue-500/30 text-blue-100'
                            : 'bg-red-500/30 text-red-100'
                          : vote.result === 'rejected'
                            ? 'bg-amber-500/20 text-amber-100'
                            : 'text-avalon-text';
                        
                        return (
                          <th key={index} className={`text-center py-1 px-1 text-xs font-medium min-w-[70px] rounded-t-md ${headerClass}`}>
                            第{vote.round}轮
                            {votes.filter(v => v.round === vote.round).length > 1 && (
                              <span className="text-[9px] opacity-80">
                                第{votes.filter((v, i) => v.round === vote.round && i <= index).length}次
                              </span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                    {/* V190: 组队信息行 - 只显示队员，不显示队长前缀 */}
                    <tr className="border-b border-avalon-purple/30 bg-[#2a2236]">
                      <td className="py-0.5 px-1.5 text-avalon-text text-[10px] sticky left-0 bg-[#2a2236] z-10 font-medium">
                        组队
                      </td>
                      {votes.map((vote, index) => {
                        // V190: 只显示队员编号，不显示"X(队)+"前缀
                        const teamNums = vote.team.map(p => p.match(/(\d+)/)?.[1] || p).join(',');
                        
                        return (
                          <td key={index} className="py-0.5 px-1 text-center">
                            <span className="text-avalon-text text-[9px] whitespace-nowrap">
                              {teamNums}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player, playerIndex) => (
                      <tr key={playerIndex} className="border-b border-avalon-purple/10 last:border-b-0">
                        <td className="py-1 px-1.5 text-white text-xs sticky left-0 bg-avalon-card z-10 font-medium">
                          {player}
                        </td>
                        {votes.map((vote, index) => {
                          const playerVote = vote.votes[player];
                          const isInTeam = vote.team.includes(player);
                          const isLeader = vote.leader === player;
                          
                          // V154: 统一视觉规范
                          let cellContent = '-';
                          let cellClass = 'bg-avalon-bg-dark/30 text-avalon-text';
                          let captainBorder = '';
                          
                          if (playerVote === 'approve') {
                            cellContent = '✓';
                            cellClass = 'bg-blue-500/30 text-blue-400';
                          } else if (playerVote === 'reject') {
                            cellContent = '✗';
                            cellClass = 'bg-red-500/30 text-red-400';
                          } else if (isLeader && vote.isForceRound) {
                            if (isInTeam) {
                              // 队长也是队员：紫底 + 金边
                              cellContent = '';
                              cellClass = 'bg-purple-500/20 text-purple-400';
                              captainBorder = 'border border-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.3)]';
                            } else {
                              // 队长不是队员：金边 + "-"符号
                              cellContent = '-';
                              cellClass = 'bg-avalon-bg-dark/50 text-avalon-text';
                              captainBorder = 'border border-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.3)]';
                            }
                          } else if (isLeader) {
                            // 普通队长（非强制轮）
                            cellContent = '';
                            cellClass = 'bg-avalon-bg-dark/50 text-avalon-text';
                            captainBorder = 'border border-yellow-400 shadow-[0_0_4px_rgba(250,204,21,0.3)]';
                          } else if (isInTeam) {
                            cellContent = '';
                            cellClass = 'bg-purple-500/20 text-purple-400';
                          }
                          
                          return (
                            <td key={index} className="py-1 px-0.5 text-center">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-bold ${cellClass} ${captainBorder}`}>
                                {cellContent}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* V145: 图例说明 - 移除'队长+队员'项 */}
              <div className="mt-1.5 pt-1.5 border-t border-avalon-purple/20 flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                <span className="text-avalon-text">图例:</span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-blue-500/30"></span>
                  <span className="text-avalon-text">支持</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-red-500/30"></span>
                  <span className="text-avalon-text">反对</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded border border-yellow-400 bg-avalon-bg-dark/50"></span>
                  <span className="text-avalon-text">队长</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-purple-500/20"></span>
                  <span className="text-avalon-text">队员</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-avalon-bg-dark/30"></span>
                  <span className="text-avalon-text">未参与</span>
                </span>
              </div>
            </div>
          )}
          
          {/* 卡片视图（原有逻辑） */}
          {voteViewMode === 'card' && [...votes].reverse().map((vote, index) => {
            const actualIndex = votes.length - 1 - index;
            const stats = getVoteStats(vote);
            const details = getVoteDetails(vote);
            const hasMissionResult = vote.missionResult && vote.missionResult !== 'pending';
            const voteRecordClass = hasMissionResult
              ? vote.missionResult === 'success'
                ? 'border-l-4 border-l-blue-500'
                : 'border-l-4 border-l-red-500'
              : '';
            
            // V139: 计算当前任务的投票次序（该轮第几次投票）
            const currentTaskVotes = votes.filter(v => v.round === vote.round);
            const voteRoundNumber = currentTaskVotes.findIndex(v => v === vote) + 1;
            
            return (
              <div key={actualIndex} className={`avalon-card ${voteRecordClass}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    {/* V145: 轮次标题统一格式：第X轮 第X次 队长X号 */}
                    <span className="text-white font-medium">第{vote.round}轮 第{voteRoundNumber}次 队长{vote.leader}</span>
                    {vote.isForceRound && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-400 text-[10px]">
                        强制轮
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        vote.result === 'approved'
                          ? 'bg-blue-500/30 text-blue-400'
                          : 'bg-red-500/30 text-red-400'
                      }`}
                    >
                      {vote.result === 'approved' ? '通过' : '否决'}
                    </span>
                    <button
                      onClick={() => startEditVote(actualIndex)}
                      className="p-0.5 rounded hover:bg-avalon-purple/50 text-avalon-text hover:text-white transition-colors"
                      title="编辑投票"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="text-avalon-text text-xs mb-1">
                  队员: {[...vote.team].sort((a, b) => parseInt(a.replace(/[^0-9]/g, '')) - parseInt(b.replace(/[^0-9]/g, ''))).join('、')}
                </div>
                
                {!vote.isForceRound && (
                  <>
                    <div className="flex gap-3 text-xs mb-1">
                      <span className="text-blue-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        支持 {stats.approve}
                      </span>
                      <span className="text-red-400 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        反对 {stats.reject}
                      </span>
                    </div>
                    <div className="bg-avalon-bg-dark/50 rounded-lg p-1.5 text-xs">
                      <div className="mb-1">
                        <span className="text-blue-400">支持 ({stats.approve}人): </span>
                        <span className="text-avalon-text">
                          {details.approvePlayers.length > 0 ? details.approvePlayers.join('、') : '无'}
                        </span>
                      </div>
                      <div>
                        <span className="text-red-400">反对 ({stats.reject}人): </span>
                        <span className="text-avalon-text">
                          {details.rejectPlayers.length > 0 ? details.rejectPlayers.join('、') : '无'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                
                {vote.result === 'approved' && (
                  <div className="mt-2 pt-2 border-t border-avalon-purple/20">
                    <button
                      onClick={() => openMissionResultModal(actualIndex)}
                      className={`w-full py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        hasMissionResult
                          ? vote.missionResult === 'success'
                            ? 'bg-blue-500/30 text-blue-400 border border-blue-500'
                            : 'bg-red-500/30 text-red-400 border border-red-500'
                          : 'bg-avalon-purple/30 text-avalon-text hover:bg-avalon-purple/50'
                      }`}
                    >
                      {hasMissionResult
                        ? vote.missionResult === 'success'
                          ? '任务成功 ✓'
                          : `任务失败 ✗ (${vote.missionFailVotes || 1}反对)`
                        : '任务结果'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showRoleSelector !== null && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRoleSelector(null)}
        >
          <div
            className="bg-avalon-card rounded-xl p-5 w-full max-w-sm max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">
                标记 {players[showRoleSelector]} 的身份
              </h3>
              <button
                onClick={() => setShowRoleSelector(null)}
                className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-blue-400 text-sm font-medium">正义阵营</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {getAvailableRoles()
                  .filter((roleId) => ROLES[roleId].faction === 'good')
                  .map((roleId) => {
                    const role = ROLES[roleId];
                    const currentRoles = playerRoleMarks[showRoleSelector] || [];
                    const isSelected = currentRoles.includes(roleId);
                    return (
                      <button
                        key={roleId}
                        onClick={() => togglePlayerRole(showRoleSelector, roleId)}
                        className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                          isSelected
                            ? 'bg-blue-500 text-white'
                            : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-blue-500/30 hover:text-white'
                        }`}
                      >
                        <span className="text-lg">{role.icon}</span>
                        <span className="text-xs truncate w-full text-center">{role.name}</span>
                      </button>
                    );
                  })}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-red-400 text-sm font-medium">邪恶阵营</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {getAvailableRoles()
                  .filter((roleId) => ROLES[roleId].faction === 'evil')
                  .map((roleId) => {
                    const role = ROLES[roleId];
                    const currentRoles = playerRoleMarks[showRoleSelector] || [];
                    const isSelected = currentRoles.includes(roleId);
                    return (
                      <button
                        key={roleId}
                        onClick={() => togglePlayerRole(showRoleSelector, roleId)}
                        className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                          isSelected
                            ? 'bg-red-500 text-white'
                            : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30 hover:text-white'
                        }`}
                      >
                        <span className="text-lg">{role.icon}</span>
                        <span className="text-xs truncate w-full text-center">{role.name}</span>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* V91: 标记身份窗口增加确认按钮 */}
            <div className="flex gap-2 mt-4">
              {(playerRoleMarks[showRoleSelector] || []).length > 0 && (
                <button
                  onClick={() => {
                    const newMarks = { ...playerRoleMarks };
                    delete newMarks[showRoleSelector];
                    setPlayerRoleMarks(newMarks);
                    savePlayerRoleMarks(newMarks);
                  }}
                  className="flex-1 py-2 rounded-lg bg-avalon-purple/50 text-avalon-text hover:bg-avalon-purple hover:text-white transition-colors text-sm"
                >
                  清除所有标记
                </button>
              )}
              <button
                onClick={() => setShowRoleSelector(null)}
                className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-yellow-400 transition-colors text-sm"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditVoteModal && editingVote && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowEditVoteModal(false)}
        >
          <div
            className="bg-avalon-card rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">
                编辑第 {editingVote.vote.round} 轮投票
              </h3>
              <button
                onClick={() => setShowEditVoteModal(false)}
                className="w-8 h-8 rounded-lg bg-avalon-bg-dark/50 flex items-center justify-center text-avalon-text hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-avalon-text text-xs mb-1 block">任务队员</label>
              <input
                type="text"
                value={editingVote.vote.team.join('、')}
                onChange={(e) => {
                  const team = e.target.value.split(/[,，、]/).map(s => s.trim()).filter(Boolean);
                  setEditingVote({
                    ...editingVote,
                    vote: { ...editingVote.vote, team }
                  });
                }}
                className="w-full bg-avalon-bg-dark/50 rounded-lg px-3 py-2 text-white text-sm outline-none focus:ring-2 focus:ring-avalon-gold"
                placeholder="用逗号或顿号分隔"
              />
            </div>

            <div className="mb-4">
              <label className="text-avalon-text text-xs mb-1 block">队长</label>
              <input
                type="text"
                value={editingVote.vote.leader}
                onChange={(e) => {
                  setEditingVote({
                    ...editingVote,
                    vote: { ...editingVote.vote, leader: e.target.value }
                  });
                }}
                className="w-full bg-avalon-bg-dark/50 rounded-lg px-3 py-2 text-white text-sm outline-none focus:ring-2 focus:ring-avalon-gold"
              />
            </div>

            <div className="mb-4">
              <label className="text-avalon-text text-xs mb-1 block">投票结果</label>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditingVote({
                      ...editingVote,
                      vote: { ...editingVote.vote, result: 'approved' }
                    });
                  }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    editingVote.vote.result === 'approved'
                      ? 'bg-blue-500 text-white'
                      : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-blue-500/30'
                  }`}
                >
                  通过
                </button>
                <button
                  onClick={() => {
                    setEditingVote({
                      ...editingVote,
                      vote: { ...editingVote.vote, result: 'rejected' }
                    });
                  }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    editingVote.vote.result === 'rejected'
                      ? 'bg-red-500 text-white'
                      : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30'
                  }`}
                >
                  否决
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-avalon-text text-xs mb-1 block">个人投票</label>
              <div className="space-y-2">
                {players.map((playerName) => (
                  <div key={playerName} className="flex items-center justify-between bg-avalon-bg-dark/30 rounded-lg px-3 py-2">
                    <span className="text-white text-sm">{playerName}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newVotes: Record<string, 'approve' | 'reject'> = { 
                            ...editingVote.vote.votes, 
                            [playerName]: 'approve' 
                          };
                          setEditingVote({
                            ...editingVote,
                            vote: { ...editingVote.vote, votes: newVotes }
                          });
                        }}
                        className={`p-1.5 rounded ${
                          editingVote.vote.votes[playerName] === 'approve'
                            ? 'bg-blue-500 text-white'
                            : 'bg-avalon-purple text-avalon-text'
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const newVotes: Record<string, 'approve' | 'reject'> = { 
                            ...editingVote.vote.votes, 
                            [playerName]: 'reject' 
                          };
                          setEditingVote({
                            ...editingVote,
                            vote: { ...editingVote.vote, votes: newVotes }
                          });
                        }}
                        className={`p-1.5 rounded ${
                          editingVote.vote.votes[playerName] === 'reject'
                            ? 'bg-red-500 text-white'
                            : 'bg-avalon-purple text-avalon-text'
                        }`}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEditVoteModal(false)}
                className="flex-1 py-2 rounded-lg bg-avalon-purple text-avalon-text hover:text-white transition-colors"
              >
                取消
              </button>
              <button
                onClick={saveEditVote}
                className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-avalon-gold/90 transition-colors"
              >
                保存修改
              </button>
            </div>
          </div>
        </div>
      )}

      {showMissionResultModal && selectedVoteIndex !== null && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowMissionResultModal(false)}
        >
          <div
            className="bg-avalon-card rounded-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-semibold text-center mb-6">
              第 {votes[selectedVoteIndex].round} 轮任务结果
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => setTempMissionResult('success')}
                className={`py-2.5 rounded-lg font-medium transition-colors ${
                  tempMissionResult === 'success'
                    ? 'bg-blue-500 text-white'
                    : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-blue-500/30'
                }`}
              >
                成功
              </button>
              <button
                onClick={() => setTempMissionResult('fail')}
                className={`py-2.5 rounded-lg font-medium transition-colors ${
                  tempMissionResult === 'fail'
                    ? 'bg-red-500 text-white'
                    : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30'
                }`}
              >
                失败
              </button>
            </div>

            {tempMissionResult === 'fail' && (
              <div className="mb-6">
                <label className="text-avalon-text text-xs mb-1 block text-center">
                  失败票数
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      onClick={() => setTempMissionFailVotes(num)}
                      className={`py-2 rounded-lg text-lg font-medium transition-colors ${
                        tempMissionFailVotes === num
                          ? 'bg-red-500 text-white'
                          : 'bg-avalon-bg-dark/50 text-avalon-text hover:bg-red-500/30'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={clearMissionResult}
                className="flex-1 py-2 rounded-lg bg-avalon-purple text-avalon-text hover:text-white transition-colors"
              >
                清除标记
              </button>
              <button
                onClick={() => setShowMissionResultModal(false)}
                className="flex-1 py-2 rounded-lg bg-avalon-bg-dark/50 text-avalon-text hover:text-white transition-colors"
              >
                取消
              </button>
              <button
                onClick={saveMissionResult}
                disabled={tempMissionResult === 'pending'}
                className="flex-1 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-avalon-gold/90 transition-colors disabled:opacity-50"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {showResetConfirm && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowResetConfirm(false)}
        >
          <div
            className="bg-avalon-card rounded-xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[#E24A4A]/20 flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-[#E24A4A]" />
              </div>
            </div>
            <h3 className="text-white font-semibold text-center mb-2">
              确定要清空所有游戏记录数据并重置吗？
            </h3>
            <p className="text-avalon-text text-sm text-center mb-6">
              此操作不可恢复，将清空所有任务标记、玩家信息、投票记录和笔记内容。
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="py-2 rounded-lg bg-avalon-purple text-white font-medium hover:bg-avalon-purple/80 transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => {
                  resetGame();
                  setShowResetConfirm(false);
                }}
                className="py-2 rounded-lg bg-[#E24A4A] text-white font-medium hover:bg-[#C23A3A] transition-colors"
              >
                确定重置
              </button>
            </div>
          </div>
        </div>
      )}

      <FloatingTimer isVisible={showFloatingTimer} onClose={() => setShowFloatingTimer(false)} />

      {isNotePanelOpen && (
        <div
          ref={notePanelRef}
          className="fixed bg-white/95 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden"
          style={{
            right: `${notePosition.x}px`,
            top: `${notePosition.y}px`,
            width: '340px',
            height: '450px',
          }}
        >
          <div
            className="note-drag-handle flex items-center justify-between px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white cursor-move select-none"
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              <span className="text-sm font-medium">游戏笔记</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsNotePanelOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveNoteTab('public')}
              className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                activeNoteTab === 'public'
                  ? 'bg-amber-100 text-amber-700 border-b-2 border-amber-500'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              公共
            </button>
            {players.map((_player, index) => (
              <button
                key={index}
                onClick={() => setActiveNoteTab(`player_${index}`)}
                className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  activeNoteTab === `player_${index}`
                    ? 'bg-amber-100 text-amber-700 border-b-2 border-amber-500'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {getPlayerTabName(index)}
              </button>
            ))}
          </div>

          <textarea
            value={getCurrentNoteContent()}
            onChange={(e) => updateNoteContent(e.target.value)}
            placeholder={activeNoteTab === 'public' ? '记录全局游戏信息...' : `记录针对 ${getPlayerTabName(parseInt(activeNoteTab.replace('player_', '')) || 0)} 的笔记...`}
            className="flex-1 p-3 text-sm text-gray-800 bg-transparent resize-none outline-none leading-relaxed"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          />

          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200">
            <span className="text-xs text-gray-500">
              {getCurrentNoteContent().length} 字
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const newNotes = { ...playerNotes };
                  newNotes[activeNoteTab] = '';
                  setPlayerNotes(newNotes);
                }}
                className="px-2 py-1 text-xs text-gray-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              >
                清空当前
              </button>
              <button
                onClick={() => {
                  const allNotes = Object.entries(playerNotes)
                    .map(([key, content]) => {
                      if (!content) return '';
                      const title = key === 'public' ? '【公共笔记】' : `【${getPlayerTabName(parseInt(key.replace('player_', '')) || 0)}】`;
                      return `${title}\n${content}`;
                    })
                    .filter(Boolean)
                    .join('\n\n---\n\n');
                  if (!allNotes) return;
                  const blob = new Blob([allNotes], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `阿瓦隆笔记_${new Date().toLocaleDateString()}.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-2 py-1 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded transition-colors"
              >
                导出全部
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </ThemedBackground>
  );
}
